import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';
import { FaPaperPlane, FaRobot, FaUser, FaSpinner, FaMicrophone, FaStop, FaVolumeUp, FaSearch, FaTimes, FaMusic, FaPlay, FaEllipsisV, FaTrash } from 'react-icons/fa';
import ChatBubble from '../components/ChatBubble';
import SelectedParagraph from '../components/SelectedParagraph';
import MobileTextAnalysis from '../components/MobileTextAnalysis';
import SongListeningSection from '../components/SongListeningSection';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  audio_url?: string;
  created_at: string;
}

interface SelectedText {
  text: string;
  position: { x: number; y: number };
}

interface Song {
  id: string;
  creator: string;
  language: string;
  song_lyrics: string;
  song_cover: string;
  is_free: boolean;
  song_url: string;
}

export default function TeacherChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [selectedParagraph, setSelectedParagraph] = useState<SelectedText | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Song[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedSong, setSelectedSong] = useState<Song | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showSongPlayer, setShowSongPlayer] = useState(false);
  const [activeSong, setActiveSong] = useState<Song | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isMobileReportOpen, setIsMobileReportOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingConversations, setDeletingConversations] = useState(false);
  const [menuButtonRect, setMenuButtonRect] = useState<DOMRect | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  
  const user = getCurrentUser();
  const navigate = useNavigate();

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const checkAccess = async () => {
    if (!user) {
      setCheckingAccess(false);
      return;
    }

    try {
      // Check if user is admin
      if (user.is_admin) {
        setHasAccess(true);
        setCheckingAccess(false);
        return;
      }

      // Check for valid coupon usage
      const { data: couponUsage, error } = await supabase
        .from('stripeencouponusage')
        .select('*')
        .eq('user_id', user.id)
        .gte('valid_until', new Date().toISOString());

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setHasAccess(!!couponUsage && couponUsage.length > 0);
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  // Handle report modal state for mobile
  const handleReportModalToggle = (isOpen: boolean) => {
    setShowReportModal(isOpen);
    if (isMobile) {
      setIsMobileReportOpen(isOpen);
    }
  };

  useEffect(() => {
    loadChatHistory();
    scrollToBottom();
    checkAccess();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearch(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle text selection for paragraph analysis
  useEffect(() => {
    const handleTextSelection = () => {
      const selection = window.getSelection();
      if (selection && selection.toString().trim().length > 5) {
        const selectedText = selection.toString().trim();
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        
        setSelectedParagraph({
          text: selectedText,
          position: {
            x: rect.left + rect.width / 2,
            y: rect.top
          }
        });
      }
    };

    document.addEventListener('mouseup', handleTextSelection);
    return () => document.removeEventListener('mouseup', handleTextSelection);
  }, []);

  const searchSongs = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const { data, error } = await supabase
        .from('stripeensongs')
        .select('id, creator, language, song_lyrics, song_cover, is_free, song_url')
        .or(`creator.ilike.%${query}%, song_lyrics.ilike.%${query}%`)
        .eq('is_visible', true)
        .limit(10);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search songs');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      searchSongs(value);
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const handleSongSelect = (song: Song) => {
    setSelectedSong(song);
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    
    // Add song context to chat with full lyrics for complete analysis
    const songMessage = `I'd like to practice with the song "${song.creator}" in ${song.language}. Please analyze and explain these complete lyrics paragraph by paragraph, helping me understand the meaning, vocabulary, and grammar:\n\n${song.song_lyrics}\n\nPlease break down each paragraph and explain the meaning, key vocabulary words, and any important grammar patterns. Help me understand the story and emotions in the song.`;
    sendMessage(songMessage);
  };

  const handleListenToSong = (song: Song) => {
    setActiveSong(song);
    setShowSongPlayer(true);
    setShowSearch(false);
  };
  const loadChatHistory = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('aiteacherenglish')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true })
        .limit(50);

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (messageContent: string, audioUrl?: string) => {
    if (!user || !messageContent.trim()) return;

    const userMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: messageContent.trim(),
      audio_url: audioUrl,
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      // Save user message to database
      const { data: savedUserMessage, error: userError } = await supabase
        .from('aiteacherenglish')
        .insert({
          user_id: user.id,
          role: 'user',
          content: messageContent.trim(),
          audio_url: audioUrl
        })
        .select()
        .single();

      if (userError) throw userError;

      // Generate AI response
      const aiResponse = await generateAIResponse(messageContent);
      
      const assistantMessage: ChatMessage = {
        id: `temp-ai-${Date.now()}`,
        role: 'assistant',
        content: aiResponse,
        created_at: new Date().toISOString()
      };

      setMessages(prev => [...prev.slice(0, -1), { ...userMessage, id: savedUserMessage.id }, assistantMessage]);

      // Save AI message to database
      const { data: savedAIMessage, error: aiError } = await supabase
        .from('aiteacherenglish')
        .insert({
          user_id: user.id,
          role: 'assistant',
          content: aiResponse
        })
        .select()
        .single();

      if (aiError) throw aiError;

      // Update with real AI message ID
      setMessages(prev => [
        ...prev.slice(0, -1),
        { ...assistantMessage, id: savedAIMessage.id }
      ]);

    } catch (error: any) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const generateAIResponse = async (userMessage: string): Promise<string> => {
    try {
      const response = await fetch('/api/generate-ai-response', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: userMessage
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate AI response');
      }

      const data = await response.json();
      return data.response;
    } catch (error) {
      console.error('Error generating AI response:', error);
      return "I'm sorry, I'm having trouble responding right now. Please try again in a moment.";
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputMessage.trim()) {
      sendMessage(inputMessage);
    }
  };

  const handlePlayAudio = async (messageId: string, audioUrl: string) => {
    try {
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.play();
        setPlayingMessageId(messageId);
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      toast.error('Failed to play audio');
    }
  };

  const handleAudioEnd = () => {
    setPlayingMessageId(null);
    setIsPlaying(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // For now, just send a message indicating voice message was recorded
        sendMessage('[Voice message recorded]', audioUrl);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleMenuToggle = () => {
    if (!showMenu && menuButtonRef.current) {
      setMenuButtonRect(menuButtonRef.current.getBoundingClientRect());
    }
    setShowMenu(!showMenu);
  };

  const handleDeleteAllConversations = async () => {
    if (!user) return;
    
    setDeletingConversations(true);
    try {
      const { error } = await supabase
        .from('aiteacherenglish')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      setMessages([]);
      setShowDeleteConfirm(false);
      setShowMenu(false);
      toast.success('All conversations deleted successfully');
    } catch (error: any) {
      console.error('Error deleting conversations:', error);
      toast.error('Failed to delete conversations');
    } finally {
      setDeletingConversations(false);
    }
  };

  // Show loading screen while checking access
  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-yellow-400 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg">Checking access...</p>
        </div>
      </div>
    );
  }

  // Show access required screen if user doesn't have access
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Stars */}
          {[...Array(200)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full animate-twinkle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 3 + 1}px`,
                height: `${Math.random() * 3 + 1}px`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>

        <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
          <div className="max-w-md w-full bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-6 bg-gradient-to-r from-yellow-400/20 to-yellow-600/20 border-b border-white/10">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden border-4 border-yellow-400/50">
                  <img 
                    src="https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/Public/image_681cd52d835cd.jpg"
                    alt="AI English Teacher"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">AI Teacher Access Required</h2>
                <p className="text-white/80">Purchase a coupon to chat with your AI teacher</p>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">What you'll get:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-white/90 text-sm">Unlimited conversations with AI teacher</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-white/90 text-sm">Access to all songs and stories</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-white/90 text-sm">Interactive practice exercises</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-white/90 text-sm">30 days of unlimited access</span>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-400/20 border border-yellow-400/30 rounded-xl p-4">
                <div className="text-center">
                  <p className="text-yellow-200 text-sm font-medium mb-2">Special Price</p>
                  <p className="text-3xl font-bold text-white">$12</p>
                  <p className="text-yellow-200/80 text-xs">One-time payment for 30 days</p>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => navigate('/coupons')}
                  className="w-full px-6 py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-xl font-bold hover:from-yellow-500 hover:to-yellow-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Purchase Access Now
                </button>
                
                {user ? (
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full px-6 py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20 transition-all duration-300 border border-white/20"
                  >
                    Back to Dashboard
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full px-6 py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20 transition-all duration-300 border border-white/20"
                  >
                    Sign In
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Stars */}
        {[...Array(200)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full animate-twinkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
        
        {/* Floating AI Elements */}
        {[...Array(10)].map((_, i) => (
          <div
            key={`ai-${i}`}
            className="absolute text-yellow-400/10 animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              fontSize: `${Math.random() * 2 + 1}rem`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${8 + Math.random() * 4}s`
            }}
          >
            🤖
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="relative z-10 p-2 border-b border-gray-800 bg-black/50 backdrop-blur-lg">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            {/* Search Button */}
            <button
              onClick={() => setShowSearch(!showSearch)}
              className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 transition-all duration-200 flex-shrink-0"
              title="Search songs to practice"
            >
              <FaSearch className="w-4 h-4 text-white" />
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-yellow-400/50 flex-shrink-0">
              <img 
                src="https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/Public/image_681cd52d835cd.jpg"
                alt="AI English Teacher"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Menu Button */}
            <div className="relative">
              <button
                ref={menuButtonRef}
                onClick={handleMenuToggle}
                className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 transition-all duration-200 flex-shrink-0"
                title="Menu"
              >
                <FaEllipsisV className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Search Dropdown */}
        {showSearch && (
          <div ref={searchRef} className="max-w-4xl mx-auto mt-4">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-4">
              <div className="flex items-center space-x-3 mb-4">
                <FaMusic className="w-5 h-5 text-yellow-400" />
                <h3 className="text-lg font-semibold text-white">Search Songs to Practice</h3>
                <button
                  onClick={() => setShowSearch(false)}
                  className="ml-auto p-1 rounded-full hover:bg-white/20 transition-colors"
                >
                  <FaTimes className="w-4 h-4 text-white" />
                </button>
              </div>
              
              <div className="relative mb-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by song title, artist, or lyrics..."
                  className="w-full px-4 py-3 bg-white/20 backdrop-blur-lg border border-white/30 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {searchLoading ? (
                    <FaSpinner className="w-4 h-4 text-white animate-spin" />
                  ) : (
                    <FaSearch className="w-4 h-4 text-white/50" />
                  )}
                </div>
              </div>
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {searchResults.map((song) => (
                    <div
                      key={song.id}
                      onClick={() => handleSongSelect(song)}
                      className="flex items-center space-x-3 p-3 bg-white/10 hover:bg-white/20 rounded-lg cursor-pointer transition-all duration-200"
                    >
                      <img
                        src={song.song_cover}
                        alt={song.creator}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium truncate">{song.creator}</h4>
                        <p className="text-white/70 text-sm">{song.language}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {song.is_free && (
                          <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                            Free
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleListenToSong(song);
                          }}
                          className="p-2 sm:p-1 rounded-full bg-yellow-400/20 hover:bg-yellow-400/30 transition-colors"
                        >
                          <FaPlay className="w-5 h-5 sm:w-3 sm:h-3 text-yellow-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {searchQuery && !searchLoading && searchResults.length === 0 && (
                <div className="text-center py-4 text-white/60">
                  No songs found for "{searchQuery}"
                </div>
              )}
              
              <div className="mt-4 text-center">
                <p className="text-white/60 text-sm">
                  💡 Select a song to practice with, then highlight any text for explanations
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Chat Container */}
      <div className={`flex-1 overflow-hidden transition-all duration-300 ${showSongPlayer ? 'lg:mr-96' : ''}`}>
        <div 
          ref={chatContainerRef}
          className="relative z-10 h-full overflow-y-auto px-4 py-4"
          style={{ height: 'calc(100vh - 140px)', paddingBottom: '80px' }}
        >
          <div className="max-w-4xl mx-auto space-y-4 pb-4">
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 overflow-hidden border-4 border-yellow-400/50 animate-spin-slow relative">
                  <img 
                    src="https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/Public/image_681cd52d835cd.jpg"
                    alt="AI English Teacher"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/20 to-transparent rounded-full"></div>
                </div>
                <h2 className="text-xl font-bold mb-2">Welcome to AI Spanish Teacher!</h2>
                <p className="text-gray-400 mb-4">Start a conversation to practice your Spanish skills</p>
                <div className="bg-white/10 backdrop-blur-lg rounded-xl p-3 max-w-md mx-auto">
                  <p className="text-sm text-gray-300">
                    💡 Try asking: "Can you help me practice Spanish ?" or "Explain this grammar rule to me"
                  </p>
                </div>
              </div>
            ) : (
              messages.map((message) => (
                <ChatBubble
                  key={message.id}
                  message={message}
                  user={user}
                  isPlaying={isPlaying}
                  playingMessageId={playingMessageId}
                  onPlayAudio={handlePlayAudio}
                  generatingAudio={generatingAudio}
                  onReportModalToggle={handleReportModalToggle}
                />
              ))
            )}
            
            {loading && (
              <div className="flex justify-start">
                <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-lg rounded-2xl p-3 border border-white/20">
                  <FaSpinner className="w-4 h-4 animate-spin text-yellow-400" />
                  <span className="text-white/80">AI is thinking...</span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* Input Area */}
      <div className={`fixed bottom-0 left-0 right-0 p-3 border-t border-gray-800 bg-black/90 backdrop-blur-lg transition-all duration-300 z-50 ${showSongPlayer ? 'lg:mr-96' : ''} ${isMobile && isMobileReportOpen ? 'hidden' : showReportModal ? 'hidden' : ''}`}>
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="flex items-center space-x-3">
            <div className="flex-1 relative">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message in English..."
                className="webview-optimized w-full px-3 py-2 bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                disabled={loading}
                id="message"
              />
            </div>
            
            <button
              type="button"
              onClick={isRecording ? stopRecording : startRecording}
              className={`p-3 rounded-xl transition-all duration-200 ${
                isRecording 
                  ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                  : 'bg-white/10 hover:bg-white/20 border border-white/20'
              }`}
            >
              {isRecording ? (
                <FaStop className="w-4 h-4 text-white" />
              ) : (
                <FaMicrophone className="w-4 h-4 text-white" />
              )}
            </button>
            
            <button
              type="submit"
              disabled={loading || !inputMessage.trim()}
              className="p-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-lg hover:from-yellow-500 hover:to-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105"
            >
              {loading ? (
                <FaSpinner className="w-4 h-4 animate-spin" />
              ) : (
                <FaPaperPlane className="w-4 h-4" />
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Song Listening Section */}
      {showSongPlayer && activeSong && (
        <SongListeningSection
          song={activeSong}
          onClose={() => {
            setShowSongPlayer(false);
            setActiveSong(null);
          }}
        />
      )}

      {/* Selected Paragraph Modal */}
      {selectedParagraph && (
        <>
          {isMobile ? (
            <MobileTextAnalysis
              selectedText={selectedParagraph.text}
              onClose={() => setSelectedParagraph(null)}
            />
          ) : (
            <SelectedParagraph
              selectedText={selectedParagraph.text}
              position={selectedParagraph.position}
              onClose={() => setSelectedParagraph(null)}
            />
          )}
        </>
      )}

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnd}
        onError={() => {
          setPlayingMessageId(null);
          setIsPlaying(false);
          toast.error('Error playing audio');
        }}
      />

      {/* Dropdown Menu - Rendered as Portal */}
      {showMenu && menuButtonRect && createPortal(
        <div 
          ref={menuRef}
          className="fixed bg-black/90 backdrop-blur-lg rounded-lg shadow-lg border border-white/20 py-2 w-56 z-[9999]"
          style={{
            top: `${menuButtonRect.bottom + 8}px`,
            left: `${menuButtonRect.right - 224}px`, // 224px = w-56 (14rem * 16px)
          }}
        >
          <button
            onClick={() => {
              setShowDeleteConfirm(true);
              setShowMenu(false);
            }}
            className="w-full flex items-center px-4 py-3 text-left text-white hover:bg-white/10 transition-colors duration-200"
          >
            <FaTrash className="w-4 h-4 mr-3 text-red-400" />
            <span>Delete All Conversations</span>
          </button>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl shadow-xl max-w-md w-full border border-gray-700">
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-xl font-semibold text-white flex items-center">
                <FaTrash className="w-5 h-5 mr-3 text-red-400" />
                Delete All Conversations
              </h3>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <p className="text-gray-300 mb-4">
                  Are you sure you want to delete all your conversations with the AI teacher? This action cannot be undone.
                </p>
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                  <p className="text-red-200 text-sm">
                    ⚠️ This will permanently delete all your chat history and cannot be recovered.
                  </p>
                </div>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white font-medium transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAllConversations}
                  disabled={deletingConversations}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors duration-200 flex items-center justify-center"
                >
                  {deletingConversations ? (
                    <>
                      <FaSpinner className="animate-spin w-4 h-4 mr-2" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FaTrash className="w-4 h-4 mr-2" />
                      Delete All
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          25% { transform: translateY(-10px) rotate(5deg); }
          50% { transform: translateY(0px) rotate(0deg); }
          75% { transform: translateY(-5px) rotate(-5deg); }
        }
        
        .animate-twinkle {
          animation: twinkle 3s ease-in-out infinite;
        }
        
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}